import {
  covariance,
  dot,
  matSubtract,
  matVec,
  normalize,
  outer,
  scale,
} from './linalg';
import type { RandomSource } from './prng';

const POWER_ITERATIONS = 100;

/**
 * The dominant eigenvector of a symmetric matrix, by power iteration:
 * repeatedly applying the matrix to a vector and renormalising converges to
 * the eigenvector of the largest eigenvalue. Standard technique for exactly
 * this case (a small, symmetric covariance matrix) — no general eigensolver
 * needed for two components.
 */
function dominantEigenvector(
  matrix: readonly (readonly number[])[],
  random: RandomSource,
): { eigenvalue: number; vector: number[] } {
  const dims = matrix.length;
  let vector = normalize(Array.from({ length: dims }, () => random() - 0.5));

  for (let i = 0; i < POWER_ITERATIONS; i += 1) {
    const next = matVec(matrix, vector);
    const nextNorm = Math.sqrt(dot(next, next));
    if (nextNorm < 1e-12) break;
    vector = next.map((value) => value / nextNorm);
  }

  const eigenvalue = dot(matVec(matrix, vector), vector);
  return { eigenvalue, vector };
}

/**
 * Projects standardised feature vectors onto their top two principal
 * components — real PCA (covariance + power-iteration eigendecomposition),
 * not a layout heuristic. This is what the "similarity map" chart plots: two
 * lookalikes sit close together here when they're close across *every*
 * feature at once, not just the two an axis picker happens to show (compare
 * the "field view", which plots raw field values directly and needs no
 * projection at all).
 *
 * Deflation (subtracting the found component's contribution before finding
 * the next) is what lets the same one-eigenvector routine find a second,
 * orthogonal one.
 */
export function projectTo2D(
  standardized: readonly (readonly number[])[],
  random: RandomSource,
): { x: number; y: number }[] {
  if (standardized.length === 0) return [];

  const cov = covariance(standardized);
  const first = dominantEigenvector(cov, random);
  const deflated = matSubtract(
    cov,
    outer(scale(first.vector, first.eigenvalue), first.vector),
  );
  const second = dominantEigenvector(deflated, random);

  return standardized.map((vector) => ({
    x: dot(vector, first.vector),
    y: dot(vector, second.vector),
  }));
}
